import { Fragment, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import apiClient from '@/api/client'
import type { UseReportRange } from '@/hooks/useReportRange'
import type { APIResponse } from '@/types'
import type { DealsReport } from '@/types/deals'
import { useCurrency } from '@/contexts/CurrencyContext'

interface Props {
  range: UseReportRange
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="space-y-2 p-5">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  )
}

function Section({ title, description, empty, isEmpty, children }: { title: string; description: string; empty: string; isEmpty: boolean; children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">{isEmpty ? <p className="py-4 text-sm text-muted-foreground">{empty}</p> : children}</CardContent>
    </Card>
  )
}

const pct = (n: number, d: number) => (d > 0 ? `${((n / d) * 100).toFixed(1)}%` : '—')

const KIND_LABEL: Record<string, string> = { item: 'Item', deal: 'Deal upgrade' }
const CHANNEL_LABEL: Record<string, string> = { till: 'Till', online: 'Online', counter: 'Till', server: 'Server tablet' }

/**
 * Deals & offers tab (F-DEAL-N5) — how the deals set up in Menu → Deals
 * performed: combos sold, automatic discounts, promo codes, special prices
 * and upsell suggestions, for the selected business days.
 */
export function DealsTab({ range }: Props) {
  const { formatCurrency } = useCurrency()
  const [open, setOpen] = useState<Set<string>>(new Set())

  const query = useQuery<APIResponse<DealsReport>>({
    queryKey: ['reports-deals', range.fromISO, range.toISO],
    queryFn: () => apiClient.getDealsReport(range.fromISO, range.toISO),
    staleTime: 60_000,
  })
  const report = query.data?.data

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (query.isError || !report) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          Could not load the Deals report. Try again in a moment.
        </CardContent>
      </Card>
    )
  }

  const up = report.upsells
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Deal sales" value={formatCurrency(report.totals.deal_sales)} sub={(() => { const n = report.combos.reduce((s, c) => s + c.qty, 0); return `${n.toLocaleString()} ${n === 1 ? 'deal' : 'deals'} sold` })()} />
        <Kpi label="Discount given" value={formatCurrency(report.totals.discount_given)} sub="Automatic discounts and promo codes" />
        <Kpi label="Upsell acceptance" value={pct(up.accepted, up.shown)} sub={`${up.accepted.toLocaleString()} of ${up.shown.toLocaleString()} suggestions taken`} />
        <Kpi label="Upsell value" value={formatCurrency(up.value)} sub="Added by accepted suggestions" />
      </div>

      <Section
        title="Deals sold"
        description="Combo deals and what guests picked inside them. A deal’s sales are split across its items by their menu prices."
        isEmpty={report.combos.length === 0}
        empty="No deals sold in this period. Set up combos in Menu → Deals → Combos."
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-transparent hover:bg-transparent">
              <TableHead>Deal</TableHead>
              <TableHead className="w-[14%] text-right">Qty</TableHead>
              <TableHead className="w-[14%] text-right">Orders</TableHead>
              <TableHead className="w-[18%] text-right">Net sales</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.combos.map((c) => {
              const isOpen = open.has(c.product_id)
              return (
                <Fragment key={c.product_id}>
                  <TableRow className="cursor-pointer" onClick={() => c.parts.length > 0 && toggle(c.product_id)}>
                    <TableCell className="text-sm font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        {c.parts.length > 0 ? (
                          isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <span className="inline-block w-4" />
                        )}
                        {c.name}
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{c.qty.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{c.orders.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-sm font-medium tabular-nums">{formatCurrency(c.net_sales)}</TableCell>
                  </TableRow>
                  {isOpen &&
                    c.parts.map((p) => (
                      <TableRow key={`${c.product_id}-${p.name}`} className="bg-muted/30 hover:bg-muted/30">
                        <TableCell className="pl-10 text-sm text-muted-foreground">{p.name}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-muted-foreground">{p.units.toLocaleString()}</TableCell>
                        <TableCell />
                        <TableCell className="text-right text-sm tabular-nums text-muted-foreground">{formatCurrency(p.allocated_sales)}</TableCell>
                      </TableRow>
                    ))}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </Section>

      <Section
        title="Automatic discounts"
        description="Bills that got an automatic deal, the discount it gave, and those bills’ item sales."
        isEmpty={report.auto_discounts.length === 0}
        empty="No automatic discounts used in this period. Set them up in Menu → Deals → Automatic discounts."
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-transparent hover:bg-transparent">
              <TableHead>Discount</TableHead>
              <TableHead className="w-[14%] text-right">Orders</TableHead>
              <TableHead className="w-[18%] text-right">Discount given</TableHead>
              <TableHead className="w-[18%] text-right">Order sales</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.auto_discounts.map((a, i) => (
              <TableRow key={a.id ?? `${a.name}-${i}`}>
                <TableCell className="text-sm font-medium">{a.name}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{a.orders.toLocaleString()}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{formatCurrency(a.discount_given)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{formatCurrency(a.order_sales)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section
        title="Promo codes"
        description="Codes redeemed in this period, for comparison with the automatic discounts."
        isEmpty={report.promo_codes.length === 0}
        empty="No promo codes redeemed in this period. Promo codes are set up under Marketing."
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-transparent hover:bg-transparent">
              <TableHead>Code</TableHead>
              <TableHead className="w-[14%] text-right">Orders</TableHead>
              <TableHead className="w-[18%] text-right">Discount given</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.promo_codes.map((p) => (
              <TableRow key={p.code}>
                <TableCell className="font-mono text-sm">{p.code}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{p.orders.toLocaleString()}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{formatCurrency(p.discount_given)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section
        title="Special prices"
        description="Items sold at a happy-hour or order-type price instead of the menu price."
        isEmpty={report.price_rules.length === 0}
        empty="Nothing sold at a special price in this period. Add special prices on an item’s form under Pricing & hours."
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-transparent hover:bg-transparent">
              <TableHead>Price</TableHead>
              <TableHead>Item</TableHead>
              <TableHead className="w-[10%] text-right">Qty</TableHead>
              <TableHead className="w-[16%] text-right">Sales</TableHead>
              <TableHead className="w-[22%] text-right">vs menu price</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {report.price_rules.map((r) => (
              <TableRow key={`${r.label}-${r.product}`}>
                <TableCell className="text-sm font-medium">{r.label}</TableCell>
                <TableCell className="text-sm">{r.product}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{r.qty.toLocaleString()}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{formatCurrency(r.sales)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">
                  {r.difference_from_menu > 0 ? (
                    <span>{formatCurrency(r.difference_from_menu)} cheaper</span>
                  ) : r.difference_from_menu < 0 ? (
                    <span>{formatCurrency(-r.difference_from_menu)} more</span>
                  ) : (
                    <span className="text-muted-foreground">Same</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section
        title="Upsells"
        description="Suggestions shown on the till and online, and how often guests took them."
        isEmpty={up.pairs.length === 0}
        empty="No upsell suggestions shown in this period. Turn them on in Settings and pick suggestions in Menu → Deals → Upsells."
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-transparent hover:bg-transparent">
              <TableHead className="w-[10%]">Channel</TableHead>
              <TableHead className="w-[12%]">Kind</TableHead>
              <TableHead>Added → suggested</TableHead>
              <TableHead className="w-[9%] text-right">Shown</TableHead>
              <TableHead className="w-[9%] text-right">Taken</TableHead>
              <TableHead className="w-[9%] text-right">Rate</TableHead>
              <TableHead className="w-[14%] text-right">Value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {up.pairs.map((p, i) => (
              <TableRow key={`${p.channel}-${p.kind}-${p.trigger}-${p.suggested}-${i}`}>
                <TableCell className="text-sm">{CHANNEL_LABEL[p.channel] ?? p.channel}</TableCell>
                <TableCell className="text-sm">{KIND_LABEL[p.kind] ?? p.kind}</TableCell>
                <TableCell className="text-sm">
                  {p.trigger} → {p.suggested}
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums">{p.shown.toLocaleString()}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{p.accepted.toLocaleString()}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{pct(p.accepted, p.shown)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{formatCurrency(p.value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    </div>
  )
}
